import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog} from '../Dialog'
import {Button} from '../Button'
import {FeedbackChannel, TripbookFeedback} from '../../domain/entities/TripbookFeedback.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'

interface EditTripbookFeedbackDialogProps {
    code: string | null
    feedback: TripbookFeedback | null
    isSaving: boolean
    isDeleting: boolean
    onSave: (feedback: TripbookFeedback) => void
    onDelete: () => void
}

// Mirrors the design's `editor.esFeedback` modal — free-text comment + channel
// chips + date. Feedback is 51trips-staff-write-only; the agency only reads it.
export const EditTripbookFeedbackDialog = forwardRef<React.ElementRef<typeof Dialog<boolean>>, EditTripbookFeedbackDialogProps>(
    ({code, feedback, isSaving, isDeleting, onSave, onDelete}, ref) => {
        const {t} = useTranslation('dashboard')
        const CHANNELS: {value: FeedbackChannel; label: string}[] = [
            {value: 'whatsapp', label: t('dialogs.channelWhatsapp')},
            {value: 'email', label: t('email')},
            {value: 'call', label: t('dialogs.channelCall')},
            {value: 'review', label: t('dialogs.channelReview')},
            {value: 'other', label: t('dialogs.channelOther')},
        ]
        const dialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)

        const [comment, setComment] = useState('')
        const [channel, setChannel] = useState<FeedbackChannel>('whatsapp')
        const [feedbackDate, setFeedbackDate] = useState('')

        useEffect(() => {
            setComment(feedback?.comment ?? '')
            setChannel(feedback?.channel ?? 'whatsapp')
            setFeedbackDate(feedback?.feedbackDate ?? '')
        }, [feedback])

        useImperativeHandle(ref, () => ({
            open: () => dialog.current!.open(),
            close: dialog.current!.close
        }), [])

        const isValid = comment.trim().length > 0

        const submit = () => {
            if (!isValid) return
            onSave({comment: comment.trim(), channel, feedbackDate: feedbackDate || null})
        }

        return (
            <Dialog
                ref={dialog}
                title={
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xl font-medium">{t('packs.sidePanelFeedback')}</span>
                        <span className="text-[12.5px] text-neutralBlack-500">{code}</span>
                    </div>
                }
                width="mid-wide"
                withHeaderDelimiter
                headerButtons={[
                    <Button key="complete" variant="primary" size="small" className="max-h-[32px]"
                            disabled={!isValid || isSaving} onClick={submit}>
                        {isSaving ? <IconSpinner className="size-4"/> : t('dialogs.save')}
                    </Button>,
                    <Button key="close" variant="secondary" size="small" className="max-h-[32px]"
                            onClick={() => dialog.current!.close()}>
                        {t('cancel')}
                    </Button>,
                ]}
            >
                <div className="flex flex-col gap-4 max-w-[420px]">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-xs text-neutralBlack-500">{t('dialogs.commentLabel')}</span>
                        <textarea
                            rows={4}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={t('dialogs.commentPlaceholder')}
                            className="border border-neutralBlack-200 rounded-lg px-3 py-2.5 text-sm leading-relaxed bg-white outline-none resize-y focus:border-neutralBlack-900"
                        />
                    </label>

                    <div className="flex flex-col gap-2">
                        <span className="text-xs text-neutralBlack-500">{t('dialogs.channelLabel')}</span>
                        <div className="flex gap-1.5 flex-wrap">
                            {CHANNELS.map((c) => (
                                <button
                                    key={c.value}
                                    type="button"
                                    onClick={() => setChannel(c.value)}
                                    className={`h-8 px-3 rounded-full text-xs whitespace-nowrap border ${
                                        channel === c.value
                                            ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                            : 'bg-white border-neutralBlack-200 text-neutralBlack-700 hover:border-neutralBlack-400'
                                    }`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className="flex flex-col gap-1.5 max-w-[200px]">
                        <span className="text-xs text-neutralBlack-500">{t('date')}</span>
                        <input type="date" value={feedbackDate} onChange={(e) => setFeedbackDate(e.target.value)}
                               className="h-10 border border-neutralBlack-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:border-neutralBlack-900"/>
                    </label>

                    <span className="text-xs text-neutralBlack-400">
                        {t('dialogs.feedbackStaffOnly')}
                    </span>

                    {feedback && (
                        <button
                            type="button"
                            disabled={isDeleting}
                            onClick={onDelete}
                            className="self-start h-9 px-4 rounded-full border border-danger/30 text-danger text-sm hover:bg-danger/10 disabled:opacity-50"
                        >
                            {isDeleting ? t('dialogs.deleting') : t('dialogs.deleteFeedback')}
                        </button>
                    )}
                </div>
            </Dialog>
        )
    }
)
